import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';

import { IconButton, MenuItem, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';
import { StyledTextField } from 'components/Input/styles';

import AuditQuestionActivities from 'stores/AuditQuestionActivities';
import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { EntityModules, isRegisterEntityModule, RegisterEntityModule } from 'core/constants';

import { useStore } from 'utils/hooks/store';

import { UuidableName } from 'utils/api/types';

import AuditsLookupField from '../../AuditsLookupField';
import { entityRecordPath } from '../../entityRecordPath';
import { EmptyState, EmptyTabState, InlineError, RetryRow, ScrollableContainer, TabBody } from '../styles';
import {
  AddArea,
  AnswerTag,
  CreateActions,
  HoverActions,
  ItemLink,
  ItemName,
  ItemRight,
  ItemRow,
  RelateCard,
  RelateField,
  RelateFieldLabel,
  SectionHeader,
  SectionHeaderLeft,
  TypeCount,
  TypeName,
  TypeSection,
} from './styles';

type LinkedItemsTabProps = {
  activities: AuditQuestionActivities;
  questionKey: string;
  lookupRelations: UuidableName[];
  lookupModule?: EntityModules;
  readOnly?: boolean;
};

type LinkedRowBase = {
  uuid: string;
  name: string;
  module: string;
};

type LinkedRow = (LinkedRowBase & { source: 'answer' }) | (LinkedRowBase & { source: 'manual'; linkUuid: string });

const typeLabel = (module: string, count: number): string => {
  const translation = getEntityTranslation[module as EntityModules];
  if (!translation) {
    return module;
  }

  return count === 1 ? translation.singular : translation.plural;
};

const NAME_TOOLTIP_LENGTH = 40;

const RowName = ({ row, path }: { row: LinkedRow; path?: string }): React.ReactElement => {
  const name = path ? (
    <ItemLink to={path} target="_blank" rel="noopener noreferrer">
      {row.name}
    </ItemLink>
  ) : (
    <ItemName>{row.name}</ItemName>
  );

  return row.name.length > NAME_TOOLTIP_LENGTH ? (
    <Tooltip title={row.name} placement="top">
      {name}
    </Tooltip>
  ) : (
    name
  );
};

/** Two sources, one list: read-only relations the question's answer carries, and removable manual
 * links. The backend rejects a manual link that duplicates the answer. */
const LinkedItemsTab = ({
  activities,
  questionKey,
  lookupRelations: rawLookupRelations,
  lookupModule,
  readOnly = false,
}: LinkedItemsTabProps): React.ReactElement => {
  const {
    session: { user },
  } = useStore();

  // Any register record can be linked to any question, so the type is picked here rather than taken
  // from the question. Audits are excluded: the record's Related tab has no audits card, so offering
  // them here would create links only this drawer could ever show.
  const userModules = user?.activeEntityModules || [];
  const routableModules = new Set<string>(userModules);
  // activeEntityModules hides Sites, Assets and Contacts from the User role, but the backend lets
  // every role see and link them, so the org's modules fill the gap.
  const targets = [
    ...userModules,
    ...(user?.activeOrganisationEntityModules || []).filter((module) => !routableModules.has(module)),
  ].filter(isRegisterEntityModule);
  const [adding, setAdding] = React.useState(false);
  const [draftType, setDraftType] = React.useState<EntityModules | ''>('');
  const [draftItems, setDraftItems] = React.useState<UuidableName[]>([]);
  const [relating, setRelating] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());

  // `linksFailed` is the terminal condition: without it a rejected load leaves `links` undefined and
  // `linksLoading` false, so this effect refires for as long as the drawer is open.
  React.useEffect(() => {
    activities.ensureLinks(questionKey);
  }, [activities, questionKey, activities.links, activities.linksLoading, activities.linksFailed]);

  const manualLinks = activities.links || [];

  // Only a lookup answer names records; any other type would turn its answer text into a fake row.
  const answerRows: LinkedRow[] = lookupModule
    ? rawLookupRelations.map((record) => ({
        uuid: record.uuid,
        name: record.name,
        module: lookupModule,
        source: 'answer' as const,
      }))
    : [];

  const rows: LinkedRow[] = [
    ...answerRows,
    ...manualLinks.map((link) => ({
      uuid: link.entity.uuid,
      name: link.entity.name,
      module: link.entity.entityType,
      source: 'manual' as const,
      linkUuid: link.uuid,
    })),
  ];

  const linkedUuids = rows.map((row) => row.uuid);

  const present = [...new Set(rows.map((row) => row.module))];
  const orderedModules = [
    ...targets.filter((module) => present.includes(module)),
    ...present.filter((module) => !targets.includes(module as RegisterEntityModule)),
  ];

  const commit = React.useRef(0);

  const resetCard = (): void => {
    commit.current += 1;
    setAdding(false);
    setDraftType('');
    setDraftItems([]);
    activities.clearLinkError();
  };

  const openCard = (): void => {
    commit.current += 1;
    setDraftType('');
    setDraftItems([]);
    activities.clearLinkError();
    setAdding(true);
  };

  const toggleCollapsed = (module: string): void => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(module)) {
        next.delete(module);
      } else {
        next.add(module);
      }

      return next;
    });
  };

  const expand = (module: string): void =>
    setCollapsed((current) => {
      if (!current.has(module)) {
        return current;
      }

      const next = new Set(current);
      next.delete(module);

      return next;
    });

  const relate = async (): Promise<void> => {
    commit.current += 1;
    const generation = commit.current;
    setRelating(true);
    let committed = 0;

    try {
      for (let index = 0; index < draftItems.length; index += 1) {
        // eslint-disable-next-line no-await-in-loop
        const created = await activities.createLink(draftItems[index].uuid);
        if (created) {
          committed += 1;
        }
        // Cancelled mid-flight: never write the draft back behind a card the user has closed.
        if (commit.current !== generation) {
          return;
        }
        if (!created) {
          setDraftItems(draftItems.slice(index));

          return;
        }
      }
      resetCard();
    } finally {
      setRelating(false);
      if (committed > 0 && draftType) {
        expand(draftType);
      }
    }
  };

  if (activities.linksFailed) {
    return (
      <TabBody>
        <EmptyState>Related records could not be loaded.</EmptyState>
        <RetryRow>
          <Button variant="outlined" size="small" onClick={activities.retryLinks}>
            Try again
          </Button>
        </RetryRow>
      </TabBody>
    );
  }

  if (activities.links === undefined) {
    return (
      <TabBody>
        <CenteredCircularProgress />
      </TabBody>
    );
  }

  return (
    <TabBody>
      <ScrollableContainer>
        {rows.length === 0 && !adding && (
          <EmptyTabState>
            <LinkOutlinedIcon sx={{ fontSize: '40px', color: 'text.disabled' }} />
            <p>Nothing related yet.</p>
            {!readOnly && <p>Relate the records this question is about.</p>}
          </EmptyTabState>
        )}

        {orderedModules.map((module) => {
          const items = rows.filter((row) => row.module === module);
          const isCollapsed = collapsed.has(module);

          return (
            <TypeSection key={module}>
              <SectionHeader
                type="button"
                aria-expanded={!isCollapsed}
                data-testid="audit-linked-items-section"
                data-module={module}
                onClick={() => toggleCollapsed(module)}
              >
                <SectionHeaderLeft>
                  <TypeName>{typeLabel(module, items.length)}</TypeName>
                  <TypeCount>({items.length})</TypeCount>
                </SectionHeaderLeft>
                <KeyboardArrowDownIcon
                  sx={{
                    fontSize: '20px',
                    color: 'text.secondary',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              </SectionHeader>

              {!isCollapsed &&
                // Match rows on source *and* uuid: the answer/link overlap check runs at
                // link-creation only, so answering after linking leaves two rows on one uuid.
                items.map((row) => {
                  // The record route is guarded on activeEntityModules; a link outside it lands on Unauthorized.
                  const path = routableModules.has(row.module) ? entityRecordPath(row.module, row.uuid) : undefined;

                  return (
                    <ItemRow
                      key={`${row.source}-${row.uuid}`}
                      data-testid="audit-linked-item-row"
                      data-link-source={row.source}
                      data-entity-uuid={row.uuid}
                    >
                      <RowName row={row} path={path} />
                      <ItemRight>
                        <HoverActions>
                          {/* Positioned and after the name link, so it sits above the row overlay. */}
                          {path && (
                            <Tooltip title="Open in new tab" placement="top">
                              <IconButton
                                component={Link}
                                to={path}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                tabIndex={-1}
                                aria-label={`Open ${row.name} in new tab`}
                                data-testid="audit-linked-item-open"
                              >
                                <OpenInNewIcon sx={{ width: '18px', height: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {row.source === 'manual' && !readOnly && (
                            <Tooltip title="Remove" placement="top">
                              <IconButton
                                size="small"
                                aria-label={`Remove ${row.name}`}
                                data-testid="audit-linked-item-remove"
                                data-entity-uuid={row.uuid}
                                onClick={() => activities.deleteLink(row.linkUuid)}
                                sx={{ '&:hover': { color: 'error.main' } }}
                              >
                                <CloseIcon sx={{ width: '18px', height: '18px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </HoverActions>
                        {row.source === 'answer' && (
                          <Tooltip title="From the answer. Change the answer to remove." placement="top">
                            <AnswerTag>From answer</AnswerTag>
                          </Tooltip>
                        )}
                      </ItemRight>
                    </ItemRow>
                  );
                })}
            </TypeSection>
          );
        })}
      </ScrollableContainer>

      {!readOnly && (
        <AddArea>
          {adding ? (
            <RelateCard data-testid="audit-linked-items-card">
              <RelateField>
                <RelateFieldLabel>Type</RelateFieldLabel>
                <StyledTextField
                  select
                  fullWidth
                  size="small"
                  disabled={relating}
                  value={draftType}
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ 'aria-label': 'Type' }}
                  data-testid="audit-linked-items-type"
                  onChange={(event) => {
                    setDraftType(event.target.value as EntityModules);
                    setDraftItems([]);
                    activities.clearLinkError();
                  }}
                >
                  <MenuItem value="" disabled>
                    Select type
                  </MenuItem>
                  {targets.map((module) => (
                    <MenuItem key={module} value={module}>
                      {getEntityTranslation[module]?.plural || module}
                    </MenuItem>
                  ))}
                </StyledTextField>
              </RelateField>

              <RelateField>
                <RelateFieldLabel>Record</RelateFieldLabel>
                {draftType ? (
                  <AuditsLookupField
                    key={draftType}
                    module={draftType}
                    value={draftItems}
                    excludeUuids={linkedUuids}
                    shortPlaceholder
                    disabled={relating}
                    onChange={(records) => setDraftItems(records)}
                  />
                ) : (
                  <StyledTextField fullWidth size="small" disabled placeholder="Select a type first" />
                )}
              </RelateField>

              {activities.linkError && <InlineError>{activities.linkError}</InlineError>}

              <CreateActions>
                <Button variant="text" size="small" onClick={resetCard} data-testid="audit-linked-items-cancel">
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  disabled={draftItems.length === 0 || relating}
                  onClick={relate}
                  data-testid="audit-linked-items-submit"
                >
                  Relate record
                </Button>
              </CreateActions>
            </RelateCard>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCard}
              data-testid="audit-linked-items-add"
              sx={{ width: '50%', alignSelf: 'center' }}
            >
              Relate a record
            </Button>
          )}
        </AddArea>
      )}
    </TabBody>
  );
};

export default observer(LinkedItemsTab);
